/**
 * 매장 관리자 Route
 * --
 */
import React, { useState, useEffect } from 'react';
import { AdminLayout, PrivateRoute } from 'components';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useUserAction, useUserStack } from 'store';
import { useAuthCheck } from 'hooks/AuthHooks';
import { useGetShopInformation } from 'hooks/ShopInformationHooks';
import { DOMAIN_URL, DEPLOY_TYPE, getCookie, deleteCookie } from 'utils';

// Route
import StoreManagement from './store.store';
import { OrderList } from './store.orders';
import { ProductAuditList, ProductList } from './store.products';
import { ManagerList, ManagerRegister } from './store.managers';
import { CalculateApplication, CalculateList } from './store.calculate';
import { StatisticDashboard } from './store.statistics';
import { SaleDashboard } from './store.sale';
import { SaleRegister } from '../admin.routes/admin.sale';
import Auth from './store.auth';

import { disableSubscription } from 'utils/SubscriptionManager';
import {
  MainDashboard,
  SalesDashboard,
  SalesDetailDashboard,
  ReceiptManager,
} from './store.dashboard';
// Icon
import {
  // BarcodeOutlined,
  DollarOutlined,
  FundProjectionScreenOutlined,
  IdcardOutlined,
  SettingOutlined,
  ShopOutlined,
  TruckOutlined,
  WalletOutlined,
  HomeOutlined,
} from '@ant-design/icons';

const StoreRoutes = () => {
  /* ===== Route ===== */
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  /* ===== Actions ===== */
  // 유저 Store
  const userStack = useUserStack();
  // 유저 Store Action
  const userAction = useUserAction();

  /* ===== Mutate ===== */
  // 토큰 검증
  const { authCheck } = useAuthCheck();
  // 쇼핑몰 단일 조회
  const { data: shopInformation, refetch: shopInformationRefetch } =
    useGetShopInformation(userStack?.shop_information_id);

  /* ===== Props ===== */
  /* ===== State ===== */
  // 사이드메뉴 선택 키
  const [selectedKeys, setSelectedKeys] = useState(['sales-status']);

  /* ===== Variable ===== */
  //   사이드 메뉴
  const menu = [
    {
      key: '/main',
      icon: <HomeOutlined />,
      label: '홈',
      // children: [],
      onClick: () => navigate(`/main`),
    },
    {
      key: 'store-management',
      icon: <ShopOutlined />,
      label: '매장 관리',
      children: [
        {
          key: 'sales-status',
          label: '판매현황',
          icon: null,
          onClick: () => navigate(`/store?key=sales-status`),
        },
        {
          key: 'store-status',
          label: '지점현황',
          icon: null,
          onClick: () => navigate(`/store?key=store-status`),
        },
        {
          key: 'menu-management',
          label: '메뉴관리',
          icon: null,
          onClick: () => navigate(`/store?key=menu-management`),
        },
        {
          key: 'staff-management',
          label: '직원관리',
          icon: null,
          onClick: () => navigate(`/store?key=staff-management`),
        },
      ],
    },
    {
      key: 'order-management',
      icon: <TruckOutlined />,
      label: '주문관리',
      children: [
        {
          key: 'order-list',
          label: '주문관리 목록',
          icon: null,
          onClick: () => navigate(`/order/list`),
        },
        // {
        //   key: 'order-ready',
        //   label: '대기중',
        //   icon: null,
        //   onClick: () => navigate(`/order?status=READY`),
        // },
        // {
        //   key: 'order-preparing',
        //   label: '조리중',
        //   icon: null,
        //   onClick: () => navigate(`/order?status=PREPARING`),
        // },
        // {
        //   key: 'order-completed',
        //   label: '완료',
        //   icon: null,
        //   onClick: () => navigate(`/order?status=COMPLETED`),
        // },
        // {
        //   key: 'order-delivering',
        //   label: '배송중',
        //   icon: null,
        //   onClick: () => navigate(`/order?status=DELIVERING`),
        // },
        // {
        //   key: 'order-delivered',
        //   label: '배송완료',
        //   icon: null,
        //   onClick: () => navigate(`/order?status=DELIVERED`),
        // },
        // {
        //   key: 'order-cancelled',
        //   label: '취소됨',
        //   icon: null,
        //   onClick: () => navigate(`/order?status=CANCELLED`),
        // },
      ],
    },
    // {
    //   key: 'calculate-management',
    //   icon: <DollarOutlined />,
    //   label: '정산관리',
    //   children: [
    //     {
    //       key: 'calculate-list',
    //       label: '정산내역 조회',
    //       icon: null,
    //       onClick: () => navigate(`/calculate/list`),
    //     },
    //     {
    //       key: 'application-list',
    //       label: '출금신청 목록',
    //       icon: null,
    //       onClick: () => navigate(`/calculate/application`),
    //     },
    //   ],
    // },
    // {
    //   key: 'product-management',
    //   icon: <BarcodeOutlined />,
    //   label: '메뉴/재고 관리',
    //   children: [
    //     {
    //       key: 'product-list',
    //       label: '메뉴 목록',
    //       icon: null,
    //       onClick: () => navigate(`/product/list`),
    //     },
    //     {
    //       key: 'product-audit-list',
    //       label: '메뉴심사 목록',
    //       icon: null,
    //       onClick: () => navigate(`/product/audit`),
    //     },
    //   ],
    // },
    {
      key: 'admin-management',
      icon: <IdcardOutlined />,
      label: '관리자 관리',
      children: [
        {
          key: 'admin-list',
          label: '관리자 목록',
          icon: null,
          onClick: () => navigate(`/manager/list`),
        },
        {
          key: 'admin-register',
          label: '관리자 등록',
          icon: null,
          onClick: () => navigate(`/manager/register`),
        },
      ],
    },
    {
      key: 'statistics-management',
      icon: <WalletOutlined />,
      label: '통계',
      children: [
        {
          key: 'statistics-dashboard',
          label: '대시보드',
          icon: null,
          onClick: () => navigate(`/statistics/dashboard`),
        },
      ],
    },
    {
      key: 'sales-management',
      icon: <FundProjectionScreenOutlined />,
      label: '매장매출',
      children: [
        {
          key: 'sales-dashboard',
          label: '대시보드',
          icon: null,
          onClick: () => navigate(`/sales/dashboard`),
        },
        {
          key: '/sales/dashboard/daily',
          label: '일자별',
          icon: null,
          onClick: () => navigate(`/sales/dashboard/daily`),
        },
        {
          key: '/sales/dashboard/timezone',
          label: '시간대별',
          icon: null,
          onClick: () => navigate(`/sales/dashboard/timezone`),
        },
        {
          key: '/sales/dashboard/menu',
          label: '메뉴별',
          icon: null,
          onClick: () => navigate(`/sales/dashboard/menu`),
        },
        {
          key: '/sales/dashboard/option_period',
          label: '옵션별',
          icon: null,
          onClick: () => navigate(`/sales/dashboard/option_period`),
        },

        {
          key: '/sales/dashboard/payments',
          label: '결제수단별',
          icon: null,
          onClick: () => navigate(`/sales/dashboard/payments`),
        },
        {
          key: '/sales/register',
          label: '주문장 생성',
          icon: null,
          onClick: () => navigate(`/sales/register`),
        },
      ],
    },
    {
      key: 'kiosk-management',
      icon: <SettingOutlined />,
      label: '키오스크',
      children: [
        {
          key: '/kiosk-management/receipt',
          label: '영수증 관리',
          icon: null,
          onClick: () => navigate(`/kiosk-management/receipt`),
        },
        // {
        //   key: '/kiosk-management/banner',
        //   label: '배너관리',
        //   icon: null,
        //   onClick: () => navigate(`/kiosk-management/banner`),
        // },
      ],
    },
  ];

  /* ===== Function ===== */
  /**
   * 라우트 권한 설정 + 상단 헤더 Breadcrumb 설정
   * --
   * @param {Object} props
   * @returns
   */
  const PrivateCheck = ({ auth = 'STORE_ADMIN', path, children }) => {
    return (
      <PrivateRoute auth={auth} path={path}>
        {children}
      </PrivateRoute>
    );
  };

  /**
   * 인증 토큰 검사
   * --
   */
  const handleTokenCheck = async () => {
    const { status, data } = await authCheck();

    if (status !== 200) {
      userAction.resetUser();
      deleteCookie('JM_COFFEE_ACCESS_TOKEN', {});
      return;
    }

    userAction.setUser(data);
    shopInformationRefetch();
  };

  /* ===== Hooks ===== */
  /**
   * 토큰 / 권한 체크
   * --
   */
  useEffect(() => {
    const token = getCookie('JM_COFFEE_ACCESS_TOKEN');

    // NOTE: 토큰 없을 경우 처리
    if (!token) {
      userAction.resetUser();
      disableSubscription();
      window.alert('세션이 만료되었습니다. 로그인 후 이용해주세요!');

      const protocol = window.location.protocol;
      const port = DEPLOY_TYPE === 'local' ? ':3000' : '';
      // const domain =
      //   DEPLOY_TYPE === 'development' ? `jm-coffee.${DOMAIN_URL}` : DOMAIN_URL;
      const domain =
        DEPLOY_TYPE === 'development'
          ? `mall-jm-coffee.${DOMAIN_URL}`
          : `mall.${DOMAIN_URL}`;
      return (window.location.href = `${protocol}//${domain}${port}/#/admin`);
    }

    const userInfo = userAction.getUser();
    // NOTE: 스토어에 저장된 값이 없을 경우 처리
    if (!userInfo) handleTokenCheck();

    // eslint-disable-next-line
  }, [navigate]);

  /**
   * 쇼핑몰 정보 스토어에 업데이트
   * --
   */
  useEffect(() => {
    const call = () => {
      const { data } = shopInformation;

      const { created_at, updated_at, type, ...d } = data;

      userAction.setUser({
        ...userStack,
        ...d,
      });
    };

    if (shopInformation && shopInformation.status === 200) {
      call();
    }
    // eslint-disable-next-line
  }, [shopInformation]);

  /* ===== Render ===== */
  return (
    <AdminLayout
      menu={menu}
      headerExceptPath={['certification']}
      footerExceptPath={['certification']}
      siderExceptPath={[]}
      selectedKeys={selectedKeys}
      onSelect={setSelectedKeys}
      brand={userStack?.shop_information_name}
    >
      <Routes>
        {/* Auth Check */}
        <Route path="certification" element={<Auth />} />

        <Route
          path="main"
          element={
            <PrivateCheck path={[{ title: '홈' }]}>
              <MainDashboard />
            </PrivateCheck>
          }
        />

        {/* 매장관리 */}
        <Route
          path="store"
          element={
            <PrivateCheck path={[{ title: '매장관리' }]}>
              <StoreManagement setSelectedKeys={setSelectedKeys} />
            </PrivateCheck>
          }
        />
        {/* 주문관리 */}
        <Route path="order">
          <Route
            path="list"
            element={
              <PrivateCheck
                path={[{ title: '주문관리' }, { title: '주문관리 목록' }]}
              >
                <OrderList />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 정산관리 */}
        <Route path="calculate">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '정산 관리' }, { title: '정산내역' }]}
                >
                  <CalculateList />
                </PrivateCheck>
              }
            />
          </Route>
          <Route
            path="application"
            element={
              <PrivateCheck
                path={[{ title: '정산 관리' }, { title: '출금신청 내역' }]}
              >
                <CalculateApplication />
              </PrivateCheck>
            }
          />
        </Route>
        {/* 메뉴/재고 관리 */}
        <Route path="product">
          <Route path="list">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '메뉴/재고 관리' }, { title: '메뉴 목록' }]}
                >
                  <ProductList />
                </PrivateCheck>
              }
            />
          </Route>
          <Route
            path="audit"
            element={
              <PrivateCheck
                path={[{ title: '메뉴/재고 관리' }, { title: '메뉴심사 목록' }]}
              >
                <ProductAuditList />
              </PrivateCheck>
            }
          />
        </Route>

        {/* 관리자 관리 */}
        <Route path="manager">
          <Route
            path="list"
            index
            element={
              <PrivateCheck
                path={[{ title: '관리자 관리' }, { title: '관리자 목록' }]}
              >
                <ManagerList />
              </PrivateCheck>
            }
          />
          <Route
            path=":adminId"
            element={
              <PrivateCheck
                path={[{ title: '관리자 관리' }, { title: '관리자 등록' }]}
              >
                <ManagerRegister />
              </PrivateCheck>
            }
          />
        </Route>
        <Route path="statistics">
          <Route
            path="dashboard"
            element={
              <PrivateCheck path={[{ title: '통계' }, { title: '대시보드' }]}>
                <SaleDashboard />
              </PrivateCheck>
            }
          />
        </Route>

        {/* 매장매출 관리 */}
        <Route path="sales">
          <Route path="dashboard">
            <Route
              index
              element={
                <PrivateCheck
                  path={[{ title: '매장매출' }, { title: '대시보드' }]}
                >
                  <SalesDashboard />
                </PrivateCheck>
              }
            />

            <Route
              index
              path=":type"
              element={
                <PrivateCheck
                  path={[
                    { title: '매장매출' },
                    {
                      title: `${
                        (pathname === '/sales/dashboard/main' && '대시보드') ||
                        (pathname === '/sales/dashboard/daily' && '일자별') ||
                        (pathname === '/sales/dashboard/menu' && '메뉴별') ||
                        (pathname === '/sales/dashboard/option_period' &&
                          '옵션별') ||
                        (pathname === '/sales/dashboard/timezone' &&
                          '시간대별') ||
                        (pathname === '/sales/dashboard/payments' &&
                          '결제수단별')
                      }`,

                      // pathname === '/statistics/dashboard/menu'
                      //   ? '메뉴별'
                      //   : pathname === '/statistics/dashboard/daily'
                      //   ? '일자별'
                      //   : '결제수단별',
                    },
                  ]}
                >
                  <SalesDetailDashboard />
                </PrivateCheck>
              }
            />
          </Route>
          <Route
            path="register"
            element={
              <PrivateCheck
                path={[{ title: '매장매출' }, { title: '주문장 생성' }]}
              >
                <SaleRegister />
              </PrivateCheck>
            }
          />
        </Route>

        {/*  */}
        <Route path="kiosk-management">
          <Route
            path="receipt"
            element={
              <PrivateCheck
                path={[{ title: '키오스크' }, { title: '영수증관리' }]}
              >
                <ReceiptManager />
              </PrivateCheck>
            }
          />
        </Route>
      </Routes>
    </AdminLayout>
  );
};

export default StoreRoutes;
